import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Search, FileText, Image, MessageSquare, Database, CheckCircle, Clock, AlertCircle } from 'lucide-react';
import { PROSPECTOR_ENGINE_URL } from '../../lib/engineUrls';

const operations = [
  {
    id: 'ocr_text',
    name: 'OCR Text Extraction',
    description: 'Extract text from documents and files',
    icon: FileText,
    color: 'blue',
    input: 'Document file (PDF, DOC, TXT)',
    output: 'Extracted text content',
    example: 'Upload a PDF → Get searchable text'
  },
  {
    id: 'ocr_image',
    name: 'OCR Image Processing',
    description: 'Extract text from images using optical character recognition',
    icon: Image,
    color: 'green',
    input: 'Image file (JPG, PNG, PDF)',
    output: 'Text extracted from image',
    example: 'Upload screenshot → Get text content'
  },
  {
    id: 'chatgpt_import',
    name: 'ChatGPT Import',
    description: 'Import ChatGPT conversation history to Notion',
    icon: MessageSquare,
    color: 'purple',
    input: 'ChatGPT conversation ID',
    output: 'Notion page with conversation',
    example: 'Chat ID → Notion page created'
  },
  {
    id: 'notion_sync',
    name: 'Notion Content Sync',
    description: 'Extract and process content from Notion pages and databases',
    icon: Database,
    color: 'orange',
    input: 'Notion page URL or database ID',
    output: 'Processed Notion content',
    example: 'Notion URL → Structured content'
  }
];

const getColorClasses = (color: string) => {
  const colors = {
    blue: {
      bg: 'bg-[var(--slate-dim)]',
      border: 'border-[var(--slate)]',
      text: 'text-[var(--text-sec)]',
      badge: 'bg-[var(--slate-dim)] text-[var(--text-sec)]',
      hover: 'hover:bg-[var(--slate-dim)]'
    },
    green: {
      bg: 'bg-[var(--green-dim)]',
      border: 'border-[var(--green)]',
      text: 'text-[var(--green)] dark:text-[var(--text-ter)]',
      badge: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]',
      hover: 'hover:bg-[var(--green-dim)]'
    },
    purple: {
      bg: 'bg-[var(--slate-dim)]',
      border: 'border-[var(--slate)]',
      text: 'text-[var(--text-sec)]',
      badge: 'bg-[var(--slate-dim)] text-[var(--text-sec)]',
      hover: 'hover:bg-[var(--slate-dim)]'
    },
    orange: {
      bg: 'bg-[var(--mustard-dim)]',
      border: 'border-[var(--mustard)]',
      text: 'text-[var(--mustard)]',
      badge: 'bg-[var(--mustard-dim)] text-[var(--mustard)]',
      hover: 'hover:bg-[var(--mustard-dim)]'
    }
  };
  return colors[color as keyof typeof colors] || colors.blue;
};

export default function Gathering() {
  const [selectedOperation, setSelectedOperation] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [results, setResults] = useState<any[]>([]);
  
  // Input state for each operation
  const [ocrTextFile, setOcrTextFile] = useState<File | null>(null);
  const [ocrImageFile, setOcrImageFile] = useState<File | null>(null);
  const [ocrImageUrl, setOcrImageUrl] = useState('');
  const [chatgptConversationId, setChatgptConversationId] = useState('');
  const [notionUrl, setNotionUrl] = useState('');
  const [notionConfig, setNotionConfig] = useState({ apiKey: '', databaseId: '' });

  const handleOperation = async (operationId: string) => {
    setSelectedOperation(operationId);
    setIsProcessing(true);
    
    try {
      let result;
      
      switch(operationId) {
        case 'ocr_text': {
          if (!ocrTextFile) {
            alert('Please select a document file');
            setIsProcessing(false);
            return;
          }
          // For text documents, we'd need a different endpoint or convert to image first
          // For now, use OCR extract endpoint
          const textFormData = new FormData();
          textFormData.append('image', ocrTextFile);
          const textResponse = await fetch(`${PROSPECTOR_ENGINE_URL}/api/ocr/extract`, {
            method: 'POST',
            body: textFormData
          });
          result = await textResponse.json();
          break;
        }
          
        case 'ocr_image':
          if (!ocrImageFile && !ocrImageUrl) {
            alert('Please upload an image or enter an image URL');
            setIsProcessing(false);
            return;
          }
          
          if (ocrImageFile) {
            const imageFormData = new FormData();
            imageFormData.append('image', ocrImageFile);
            const imageResponse = await fetch(`${PROSPECTOR_ENGINE_URL}/api/ocr/extract`, {
              method: 'POST',
              body: imageFormData
            });
            result = await imageResponse.json();
          } else {
            const urlResponse = await fetch(`${PROSPECTOR_ENGINE_URL}/api/ocr/extract-url`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ imageUrl: ocrImageUrl })
            });
            result = await urlResponse.json();
          }
          break;
          
        case 'chatgpt_import':
          if (!chatgptConversationId) {
            alert('Please enter a ChatGPT conversation ID');
            setIsProcessing(false);
            return;
          }
          // This would need a ChatGPT integration endpoint
          result = { success: false, error: 'ChatGPT import not yet implemented' };
          break;
          
        case 'notion_sync':
          if (!notionUrl) {
            alert('Please enter a Notion page URL or database ID');
            setIsProcessing(false);
            return;
          }
          // This would need a Notion sync endpoint
          result = { success: false, error: 'Notion sync not yet implemented' };
          break;
          
        default:
          result = { success: false, error: 'Unknown operation' };
      }
      
      setIsProcessing(false);
      setResults(prev => [...prev, {
        id: Date.now(),
        operation: operationId,
        status: result.success ? 'completed' : 'error',
        result: result,
        timestamp: new Date().toISOString()
      }]);
      
      if (result.success) {
        // Clear inputs after success
        setOcrTextFile(null);
        setOcrImageFile(null);
        setOcrImageUrl('');
        setChatgptConversationId('');
        setNotionUrl('');
      }
    } catch (error: any) {
      setIsProcessing(false);
      setResults(prev => [...prev, {
        id: Date.now(),
        operation: operationId,
        status: 'error',
        error: error.message,
        timestamp: new Date().toISOString()
      }]);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--surface)]">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link to="/elevation/workflow" className="inline-flex items-center gap-2 text-consulting-slate-blue-600 hover:text-consulting-slate-blue-700 mb-4">
            <ArrowLeft className="h-4 w-4" />
            Back to Workflow
          </Link>
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 bg-[var(--slate-dim)] flex items-center justify-center">
              <Search className="h-6 w-6 text-[var(--text-ter)]" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-[var(--text-pri)]">Gathering Content</h1>
              <p className="text-[var(--text-sec)]">Discover and capture content from various sources</p>
            </div>
          </div>
        </div>

        {/* Engine Status */}
        <div className="bg-[var(--bg-raised)] shadow-sm border p-6 mb-8">
          <h2 className="text-xl font-semibold text-[var(--text-pri)] mb-4">Prospector Engine Status</h2>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-[var(--green)] rounded-full"></div>
              <span className="text-sm text-[var(--text-sec)]">Online</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-[var(--text-ter)]" />
              <span className="text-sm text-[var(--text-sec)]">Last activity: 2 minutes ago</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-[var(--green)]" />
              <span className="text-sm text-[var(--text-sec)]">Ready for operations</span>
            </div>
          </div>
        </div>

        {/* Operations */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-[var(--text-pri)] mb-6">Available Operations</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {operations.map((operation) => {
              const Icon = operation.icon;
              const colors = getColorClasses(operation.color);
              
              return (
                <div
                  key={operation.id}
                  className={`${colors.bg} ${colors.border} border-2  p-6 ${colors.hover} transition-all duration-300`}
                >
                  <div className="flex items-center gap-4 mb-4">
                    <div className={`w-12 h-12 ${colors.badge}  flex items-center justify-center`}>
                      <Icon className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className={`text-lg font-semibold ${colors.text}`}>
                        {operation.name}
                      </h3>
                      <p className={`text-sm ${colors.text}`}>
                        {operation.description}
                      </p>
                    </div>
                  </div>
                  
                  <div className="space-y-3 mb-4">
                    <div>
                      <span className="text-xs font-medium text-[var(--text-ter)]">INPUT:</span>
                      <p className="text-sm text-[var(--text-sec)]">{operation.input}</p>
                    </div>
                    <div>
                      <span className="text-xs font-medium text-[var(--text-ter)]">OUTPUT:</span>
                      <p className="text-sm text-[var(--text-sec)]">{operation.output}</p>
                    </div>
                    <div>
                      <span className="text-xs font-medium text-[var(--text-ter)]">EXAMPLE:</span>
                      <p className="text-sm text-[var(--text-sec)]">{operation.example}</p>
                    </div>
                  </div>

                  {/* Input Fields */}
                  <div className="space-y-3 mb-4 border-t pt-4">
                    {operation.id === 'ocr_text' && (
                      <>
                        <label className="block text-sm font-medium text-[var(--text-sec)]">Upload Document</label>
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx,.txt"
                          onChange={(e) => setOcrTextFile(e.target.files?.[0] || null)}
                          className="w-full border border-[var(--rule)] px-3 py-2 text-sm"
                          disabled={isProcessing}
                        />
                        {ocrTextFile && (
                          <p className="text-xs text-[var(--text-sec)]">Selected: {ocrTextFile.name}</p>
                        )}
                      </>
                    )}
                    
                    {operation.id === 'ocr_image' && (
                      <>
                        <label className="block text-sm font-medium text-[var(--text-sec)]">Upload Image</label>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => setOcrImageFile(e.target.files?.[0] || null)}
                          className="w-full border border-[var(--rule)] px-3 py-2 text-sm mb-2"
                          disabled={isProcessing}
                        />
                        {ocrImageFile && (
                          <p className="text-xs text-[var(--text-sec)] mb-2">Selected: {ocrImageFile.name}</p>
                        )}
                        <label className="block text-sm font-medium text-[var(--text-sec)]">Or Enter Image URL</label>
                        <input
                          type="url"
                          value={ocrImageUrl}
                          onChange={(e) => setOcrImageUrl(e.target.value)}
                          placeholder="https://example.com/image.jpg"
                          className="w-full border border-[var(--rule)] px-3 py-2 text-sm"
                          disabled={isProcessing}
                        />
                      </>
                    )}
                    
                    {operation.id === 'chatgpt_import' && (
                      <>
                        <label className="block text-sm font-medium text-[var(--text-sec)]">ChatGPT Conversation ID</label>
                        <input
                          type="text"
                          value={chatgptConversationId}
                          onChange={(e) => setChatgptConversationId(e.target.value)}
                          placeholder="Enter conversation ID"
                          className="w-full border border-[var(--rule)] px-3 py-2 text-sm"
                          disabled={isProcessing}
                        />
                        {notionConfig.apiKey && (
                          <>
                            <label className="block text-sm font-medium text-[var(--text-sec)] mt-2">Notion API Key</label>
                            <input
                              type="password"
                              value={notionConfig.apiKey}
                              onChange={(e) => setNotionConfig(prev => ({ ...prev, apiKey: e.target.value }))}
                              placeholder="notion_..."
                              className="w-full border border-[var(--rule)] px-3 py-2 text-sm"
                              disabled={isProcessing}
                            />
                            <label className="block text-sm font-medium text-[var(--text-sec)] mt-2">Notion Database ID</label>
                            <input
                              type="text"
                              value={notionConfig.databaseId}
                              onChange={(e) => setNotionConfig(prev => ({ ...prev, databaseId: e.target.value }))}
                              placeholder="Database ID"
                              className="w-full border border-[var(--rule)] px-3 py-2 text-sm"
                              disabled={isProcessing}
                            />
                          </>
                        )}
                      </>
                    )}
                    
                    {operation.id === 'notion_sync' && (
                      <>
                        <label className="block text-sm font-medium text-[var(--text-sec)]">Notion Page URL or Database ID</label>
                        <input
                          type="text"
                          value={notionUrl}
                          onChange={(e) => setNotionUrl(e.target.value)}
                          placeholder="https://notion.so/page/... or database ID"
                          className="w-full border border-[var(--rule)] px-3 py-2 text-sm"
                          disabled={isProcessing}
                        />
                      </>
                    )}
                  </div>
                  
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (!isProcessing) {
                        handleOperation(operation.id);
                      }
                    }}
                    disabled={isProcessing || 
                      (operation.id === 'ocr_text' && !ocrTextFile) ||
                      (operation.id === 'ocr_image' && !ocrImageFile && !ocrImageUrl) ||
                      (operation.id === 'chatgpt_import' && !chatgptConversationId) ||
                      (operation.id === 'notion_sync' && !notionUrl)
                    }
                    className={`w-full py-2 px-4  font-medium transition-colors ${
                      isProcessing && selectedOperation !== operation.id
                        ? 'bg-[var(--hover-tint)] text-[var(--text-ter)] cursor-not-allowed'
                        : isProcessing && selectedOperation === operation.id
                        ? 'bg-[var(--rule-bright)] text-[var(--text-ter)] cursor-not-allowed'
                        : `${colors.badge} ${colors.text} hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed`
                    }`}
                  >
                    {isProcessing && selectedOperation === operation.id ? (
                      <div className="flex items-center justify-center gap-2">
                        <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                        Processing...
                      </div>
                    ) : (
                      'Execute Operation'
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Processing Results */}
        {results.length > 0 && (
          <div className="bg-[var(--bg-raised)] shadow-sm border p-6">
            <h2 className="text-xl font-semibold text-[var(--text-pri)] mb-4">Processing Results</h2>
            <div className="space-y-4">
              {results.map((result) => {
                const operation = operations.find(op => op.id === result.operation);
                const isError = result.status === 'error' || result.result?.error;
                
                return (
                  <div key={result.id} className={`p-4  border ${isError ? 'bg-[var(--coral-dim)] border-[var(--coral)]' : 'bg-[var(--surface)] border-[var(--rule)]'}`}>
                    <div className="flex items-start gap-3 mb-3">
                      {isError ? (
                        <AlertCircle className="h-5 w-5 text-[var(--coral)] flex-shrink-0 mt-0.5" />
                      ) : (
                        <CheckCircle className="h-5 w-5 text-[var(--green)] flex-shrink-0 mt-0.5" />
                      )}
                      <div className="flex-1">
                        <p className="text-sm font-medium text-[var(--text-pri)]">
                          {operation?.name} {isError ? 'failed' : 'completed'}
                        </p>
                        <p className="text-xs text-[var(--text-ter)] mt-1">
                          {new Date(result.timestamp).toLocaleString()}
                        </p>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded ${
                        isError ? 'bg-[var(--coral-dim)] text-[var(--coral)]' : 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]'
                      }`}>
                        {result.status}
                      </span>
                    </div>
                    
                    {/* Show actual results */}
                    {result.result && (
                      <div className="mt-3 pt-3 border-t border-[var(--rule)]">
                        {result.result.error && (
                          <p className="text-sm text-[var(--coral)]">Error: {result.result.error}</p>
                        )}
                        {result.result.success && result.result.result && (
                          <div className="space-y-2">
                            {result.result.result.text && (
                              <div>
                                <p className="text-xs font-medium text-[var(--text-sec)] mb-1">Extracted Text:</p>
                                <div className="bg-[var(--bg-raised)] p-3 rounded border border-[var(--rule)] max-h-64 overflow-y-auto">
                                  <pre className="text-sm text-[var(--text-pri)] whitespace-pre-wrap font-mono">
                                    {result.result.result.text}
                                  </pre>
                                </div>
                                {result.result.result.confidence && (
                                  <p className="text-xs text-[var(--text-sec)] mt-1">
                                    Confidence: {Math.round(result.result.result.confidence * 100)}%
                                  </p>
                                )}
                                <button
                                  onClick={() => navigator.clipboard.writeText(result.result.result.text)}
                                  className="mt-2 px-3 py-1 bg-[var(--slate)] text-white rounded text-xs hover:bg-[var(--slate)]"
                                >
                                  Copy Text
                                </button>
                              </div>
                            )}
                            {!result.result.result.text && (
                              <pre className="text-xs bg-[var(--bg-raised)] p-2 rounded border text-[var(--text-sec)] overflow-auto max-h-48">
                                {JSON.stringify(result.result, null, 2)}
                              </pre>
                            )}
                          </div>
                        )}
                        {result.result.success && !result.result.result && (
                          <p className="text-sm text-[var(--text-sec)]">Operation completed successfully</p>
                        )}
                      </div>
                    )}
                    {result.error && !result.result && (
                      <div className="mt-3 pt-3 border-t border-[var(--rule)]">
                        <p className="text-sm text-[var(--coral)]">Error: {result.error}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Next Steps */}
        <div className="bg-[var(--bg-raised)] shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-[var(--text-pri)] mb-4">Next Steps</h3>
          <p className="text-[var(--text-sec)] mb-4">
            Once you've gathered your content, move to the next phase to add metadata and structure.
          </p>
          <Link
            to="/elevation/workflow/tagging"
            className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--green)] text-white hover:bg-[var(--green)] transition-colors"
          >
            Go to Tagging Phase
            <ArrowLeft className="h-4 w-4 rotate-180" />
          </Link>
        </div>
      </div>
    </div>
  );
}
